import LegalPage from "./common/LegalPage";
import { ROUTES } from "../../routes/path";

const DOC = {
  title: "Privacy Policy",
  tagline: "How Global Tech9 LLC collects, uses and protects your information.",
  effective: "October 21, 2026",
  intro:
    'This Privacy Policy explains what personal information Global Tech9 LLC ("Global Tech9 LLC", "we", or "us"), a company based in Austin, Texas, collects when you visit this website or contact us, how we use it, who we share it with, and the choices you have.',
  sections: [
    {
      id: "collect",
      title: "Information We Collect",
      blocks: [
        {
          t: "ul",
          items: [
            [
              "Information you give us:",
              "When you message or call us, for example on WhatsApp or by email, we receive what you share, such as your name, phone number, email address, company, job title, and details about your project.",
            ],
            [
              "Information collected automatically:",
              "Like most websites, our web server may record technical details such as your IP address, browser type, device type, pages visited, and the date and time of your visit.",
            ],
          ],
        },
        {
          t: "p",
          text: "This website does not have user accounts, online payments, or a contact form. We do not ask you for sensitive information such as government ID numbers, payment card details, or health information through it. Please do not send us such information in your messages.",
        },
      ],
    },
    {
      id: "use",
      title: "How We Use Information",
      blocks: [
        { t: "p", text: "We use your information for the following purposes:" },
        {
          t: "ul",
          items: [
            [
              "",
              "To reply to your messages and discuss your project or a quotation;",
            ],
            [
              "",
              "To provide, secure, and improve this website, including preventing misuse;",
            ],
            [
              "",
              "To meet legal, tax, and accounting obligations, and to establish or defend legal claims.",
            ],
          ],
        },
        {
          t: "note",
          text: "We do not sell your personal information, and we do not process it for targeted advertising or for profiling that produces legal or similarly significant effects.",
        },
      ],
    },
    {
      id: "location",
      title: "Service Providers and Data Location",
      blocks: [
        {
          t: "p",
          text: "Your information is stored and processed in the United States. If any of our service providers process it outside the United States, they do so under written terms that require them to protect it in line with this Policy and applicable law.",
        },
      ],
    },
    {
      id: "sharing",
      title: "Third-Party Sharing, Cookies and Similar Technologies",
      blocks: [
        { t: "p", text: "We share personal information only with:" },
        {
          t: "ul",
          items: [
            [
              "",
              "Service providers that help us run our business (for example, website hosting and communication tools), who may use it only to provide services to us;",
            ],
            [
              "",
              "Government authorities, courts, or others when required by law or to protect our rights; and",
            ],
            [
              "",
              "A buyer or successor if our business or part of it is sold or reorganized.",
            ],
          ],
        },
        {
          t: "p",
          text: "This website loads fonts from Google Fonts, so your browser connects to Google servers, which may receive your IP address. WhatsApp (Meta) processes conversations on its own platform under its own privacy policy. We do not use advertising or tracking cookies on this website. If this changes, we will update this Policy and, where required, ask for your consent.",
        },
      ],
    },
    {
      id: "rights",
      title: "Your Privacy Rights",
      blocks: [
        {
          t: "p",
          text: "If you are a Texas resident, the Texas Data Privacy and Security Act (TDPSA) gives you the right to:",
        },
        {
          t: "ul",
          items: [
            [
              "",
              "Confirm whether we process your personal data, and access that data;",
            ],
            ["", "Correct inaccuracies in your personal data;"],
            [
              "",
              "Delete personal data you have provided to us or that we have obtained about you;",
            ],
            [
              "",
              "Obtain a copy of your personal data in a portable, readily usable format;",
            ],
            [
              "",
              "Opt out of the sale of personal data, targeted advertising, and certain profiling.",
            ],
          ],
        },
        {
          t: "p",
          text: "To make a request, contact us using the details in Section 9. We may need to verify your identity first. We will respond within 45 days, which we may extend once by up to 45 more days if we notify you. We will not treat you unfairly for exercising your rights.",
        },
        {
          t: "p",
          text: "Appeals: If we decline your request, you may appeal by replying to our decision within a reasonable time. We will respond to your appeal within 60 days. If your appeal is denied, you may contact the Texas Attorney General to submit a complaint at texasattorneygeneral.gov.",
        },
        {
          t: "p",
          text: "If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA). Contact us to exercise them.",
        },
      ],
    },
    {
      id: "security",
      title: "Security and Retention",
      blocks: [
        {
          t: "p",
          text: "We keep business enquiries for as long as needed to manage the relationship, and afterward for the period required by law for records such as contracts and invoices. When information is no longer needed, we delete or anonymise it. We use reasonable administrative, technical, and physical measures to protect information, though no method of transmission or storage is completely secure. If a data breach affects you, we will notify you and the authorities as required by Texas law.",
        },
      ],
    },
    {
      id: "children",
      title: "Children's Privacy",
      blocks: [
        {
          t: "p",
          text: "This website is intended for businesses and is not directed at children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us information, please contact us and we will delete it.",
        },
      ],
    },
    {
      id: "changes",
      title: "Changes to This Policy",
      blocks: [
        {
          t: "p",
          text: "We may update this Privacy Policy from time to time. The effective date at the top shows when it was last revised. If we make material changes, we will update the date and post the revised Policy on this website.",
        },
      ],
    },
    {
      id: "contact",
      title: "Contact Us",
      blocks: [
        {
          t: "p",
          text: "To exercise your privacy rights, raise a concern, or ask questions, please contact us:",
        },
        { t: "contact" },
      ],
    },
  ],
  footnote:
    "Questions about this policy? Use the contact details in section 9.",
  // The "Contact Us" cards read from here (instead of the shared Thane details in LegalPage.jsx)
  contact: {
    name: "Global Tech9 LLC",
    email: "contact@globaltech9.com",
    address: "5900 Balcones Drive STE 100, Austin, TX 78731, USA",
  },
};

const RELATED = {
  to: ROUTES.TERMS,
  title: "Terms and Conditions",
  text: "The rules that apply when you use this website.",
};

export default function PrivacyPolicy() {
  return <LegalPage icon="shield" related={RELATED} {...DOC} />;
}
